/**
 * Energy Role - Convert Pillar
 *
 * Conversão entre unidades de energia usando arquitetura hub-and-spoke.
 * Base: joule (J)
 *
 * @example
 * import { convertEnergy, toBaseEnergy, fromBaseEnergy } from '@attrx/role-morphic/energy/convert';
 *
 * convertEnergy('kilocalorie', 'kilojoule', 1);   // 4.1868
 * toBaseEnergy('kilowatt_hour', 1);               // 3600000
 * fromBaseEnergy('btu', 1055.06);                 // ~1
 */

import { ENERGY_UNITS, type EnergyUnit } from "./constants";

// =============================================================================
// TYPES
// =============================================================================

export type Result<T> =
  | { ok: true; value: T }
  | { ok: false; error: string };

// =============================================================================
// CONSTANTS
// =============================================================================

/** Variante base para conversões */
export const ENERGY_BASE: EnergyUnit = "joule";

// =============================================================================
// CONVERT
// =============================================================================

/**
 * Converte valor para a unidade base (joule)
 *
 * @param variant - Unidade de origem
 * @param value - Valor a converter
 * @returns Valor em joule
 *
 * @example
 * toBaseEnergy('kilowatt_hour', 1);   // 3600000
 * toBaseEnergy('kilocalorie', 1);     // 4186.8
 */
export function toBaseEnergy(variant: EnergyUnit, value: number): number {
  const unit = ENERGY_UNITS[variant];

  if (!unit) {
    throw new Error(`Unknown energy variant: ${variant}`);
  }

  return value * (unit.factor ?? 1);
}

/**
 * Converte valor da unidade base (joule) para outra unidade
 *
 * @param variant - Unidade de destino
 * @param baseValue - Valor em joule
 * @returns Valor na unidade de destino
 *
 * @example
 * fromBaseEnergy('kilowatt_hour', 3600000);   // 1
 * fromBaseEnergy('kilocalorie', 4186.8);      // 1
 */
export function fromBaseEnergy(variant: EnergyUnit, baseValue: number): number {
  const unit = ENERGY_UNITS[variant];

  if (!unit) {
    throw new Error(`Unknown energy variant: ${variant}`);
  }

  return baseValue / (unit.factor ?? 1);
}

/**
 * Converte entre duas unidades de energia
 *
 * @param from - Unidade de origem
 * @param to - Unidade de destino
 * @param value - Valor a converter
 * @returns Valor convertido
 *
 * @example
 * convertEnergy('kilocalorie', 'kilojoule', 1);      // 4.1868
 * convertEnergy('kilowatt_hour', 'megajoule', 1);    // 3.6
 * convertEnergy('btu', 'joule', 1);                  // 1055.056...
 */
export function convertEnergy(
  from: EnergyUnit,
  to: EnergyUnit,
  value: number
): number {
  // Otimização: mesma unidade
  if (from === to) {
    return value;
  }

  // Hub-and-spoke: from → base → to
  const baseValue = toBaseEnergy(from, value);
  return fromBaseEnergy(to, baseValue);
}

/**
 * Versão safe de convertEnergy que retorna Result
 *
 * @example
 * tryConvertEnergy('kilocalorie', 'kilojoule', 1);
 * // { ok: true, value: 4.1868 }
 *
 * tryConvertEnergy('invalid', 'joule', 1);
 * // { ok: false, error: 'Unknown energy variant: invalid' }
 */
export function tryConvertEnergy(
  from: EnergyUnit,
  to: EnergyUnit,
  value: number
): Result<number> {
  try {
    const result = convertEnergy(from, to, value);
    return { ok: true, value: result };
  } catch (err) {
    return { ok: false, error: String(err) };
  }
}

/**
 * Verifica se uma variante existe
 */
export function hasEnergyVariant(variant: string): variant is EnergyUnit {
  return variant in ENERGY_UNITS;
}

/**
 * Lista todas as variantes disponíveis
 */
export function getEnergyVariants(): EnergyUnit[] {
  return Object.keys(ENERGY_UNITS) as EnergyUnit[];
}
